'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'ar' | 'en';

interface LanguageContextType {
  lang: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const translations: Record<Language, Record<string, string>> = {
  ar: {
    app_name: "سيارتي 360",
    app_subtitle: "Sudan Marketplace",
    search_placeholder: "ابحث عن سيارات، قطع غيار، أو ورش...",
    main_services: "الخدمات الأساسية",
    ai_services: "خدمات الذكاء الاصطناعي",
    account: "الحساب",
    buy_car: "شراء سيارة",
    sell_car: "بيع سيارة",
    parts: "قطع الغيار",
    maintenance: "صيانة ورش",
    diagnose: "تشخيص الأعطال",
    inspection: "الفحص البصري",
    profile: "ملفي الشخصي",
    profile_sub: "My Profile & Settings",
    notifications: "التنبيهات والمقترحات",
    view_all: "مشاهدة الكل",
    admin_link: "الإدارة المركزية",
    lang_btn: "English",
  },
  en: {
    app_name: "MyCar 360",
    app_subtitle: "Sudan Marketplace",
    search_placeholder: "Search for cars, parts, or workshops...",
    main_services: "Main Services",
    ai_services: "AI Services",
    account: "Account",
    buy_car: "Buy Car",
    sell_car: "Sell Car",
    parts: "Spare Parts",
    maintenance: "Workshops",
    diagnose: "AI Diagnose",
    inspection: "AI Vision",
    profile: "My Profile",
    profile_sub: "My Profile & Settings",
    notifications: "Notifications & Suggestions",
    view_all: "View All",
    admin_link: "Central Admin",
    lang_btn: "عربي",
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Language>('ar');

  useEffect(() => {
    const savedLang = localStorage.getItem('app_lang') as Language;
    if (savedLang) {
      setLang(savedLang);
    }
  }, []);

  const toggleLanguage = () => {
    const newLang = lang === 'ar' ? 'en' : 'ar';
    setLang(newLang);
    localStorage.setItem('app_lang', newLang);
  };

  const t = (key: string) => {
    return translations[lang][key] || key;
  };

  const isRTL = lang === 'ar';

  return (
    <LanguageContext.Provider value={{ lang, toggleLanguage, t, isRTL }}>
      <div dir={isRTL ? 'rtl' : 'ltr'} className={lang}>
        {children}
      </div>
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
